import { Button } from "@/components/common"
import { Steps } from "@/components/common/Steps"
import { useConditions } from "@/hooks/useConditions"
import { useGetOrganization } from "@/hooks/useOrganizations"
import { FC, useEffect, useMemo, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useApp } from "../../AppProvider"
import { API_URL } from "../../constants"
import { Email } from "../../types/Email"
import { Patient } from "../../types/Patient"
import { STATUS } from "../../types/shared"
import { renderEmail, RenderingContext } from "../../utils/email"

interface EmailEditorProps {
  patient: Patient
  submitCb?: () => void
}

const DEFAULT_TEMPLATE = `Dear {{ patient.name }},

We have received the test results for {{ patient.name }}. The results indicate the following:

{{ conditions }}

We recommend scheduling a consultation to discuss the next steps and ensure {{ patient.name }}'s continued health. Please reach out to us to set up an appointment.

Warm regards,
{{ user.name }}
{{ org.name }}
`

export const EmailEditor: FC<EmailEditorProps> = ({ patient, submitCb }) => {
  const { user, token, addToast, orgId } = useApp()

  const { register, handleSubmit, watch, reset, setValue, setError } = useForm<Email>({
    defaultValues: {
      sendTo: patient?.email || patient?.metadata?.email || "",
      replyTo: user?.email,
      subject: "Test results follow up",
      body: DEFAULT_TEMPLATE,
    },
  })

  const { data: organization } = useGetOrganization(orgId, token)
  const { data: conditions } = useConditions(orgId, token)

  const [status, setStatus] = useState<STATUS>("DEFAULT")
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedConditions, setSelectedConditions] = useState<{ message: string }[]>([])
  const [conditionSelections, setConditionSelections] = useState<
    { conditionId: string; selectedRecommendationIndex: number; isSelected: boolean }[]
  >([])

  const steps = [
    {
      id: "Step 1",
      name: "Conditions",
      status: (currentStep > 1 ? "complete" : currentStep === 1 ? "current" : "upcoming") as
        | "complete"
        | "current"
        | "upcoming",
    },
    {
      id: "Step 2",
      name: "Email Details",
      status: (currentStep === 2 ? "current" : "upcoming") as "complete" | "current" | "upcoming",
    },
  ]

  const renderingContext: RenderingContext = useMemo(
    () => ({
      patient,
      user,
      org: organization,
      conditions: selectedConditions,
    }),
    [organization, patient, user, selectedConditions],
  )

  // Initialize condition selections when conditions are loaded
  useEffect(() => {
    if (conditions && conditionSelections.length === 0) {
      const initialSelections = conditions.map((condition) => ({
        conditionId: condition._id,
        selectedRecommendationIndex: -1,
        isSelected: false,
      }))
      setConditionSelections(initialSelections)
    }
  }, [conditions, conditionSelections.length])

  const body = watch("body")

  const handleStepClick = (stepId: string) => {
    const stepNumber = parseInt(stepId.split(" ")[1])
    setCurrentStep(stepNumber)
  }

  const handleNextStep = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const updateSelectedConditionsFromSelections = (selections: typeof conditionSelections) => {
    const newSelectedConditions = selections
      .filter((sel) => sel.isSelected && sel.selectedRecommendationIndex >= 0)
      .map((sel) => {
        const condition = conditions?.find((c) => c._id === sel.conditionId)
        const recommendation = condition?.recommendations[sel.selectedRecommendationIndex]
        return { message: recommendation?.message ?? "" }
      })
    setSelectedConditions(newSelectedConditions)
  }

  const handlePresetSelect = (conditionId: string, recommendationIndex: number) => {
    const condition = conditions?.find((c) => c._id === conditionId)
    const recommendation = condition?.recommendations[recommendationIndex]
    const currentSelection = conditionSelections.find((sel) => sel.conditionId === conditionId)

    if (recommendation) {
      // Check if this recommendation is already selected
      const isCurrentlySelected =
        currentSelection?.selectedRecommendationIndex === recommendationIndex && currentSelection?.isSelected

      // Update selection state - toggle if same recommendation, otherwise select new one
      const updatedSelections = conditionSelections.map((sel) =>
        sel.conditionId === conditionId
          ? {
              ...sel,
              selectedRecommendationIndex: isCurrentlySelected ? -1 : recommendationIndex,
              isSelected: !isCurrentlySelected,
            }
          : sel,
      )
      setConditionSelections(updatedSelections)

      // Update selected conditions array
      updateSelectedConditionsFromSelections(updatedSelections)
    }
  }

  const onSubmit: SubmitHandler<Email> = async (data) => {
    if (status === "LOADING") return

    setStatus("LOADING")
    const { sendTo, replyTo, subject, body } = data

    // Split multiple email addresses by comma and trim whitespace
    const sendToArray = (sendTo as string).split(",").map((email) => email.trim())

    if (!sendToArray.length || !replyTo || !subject || !body) {
      setError("sendTo", { message: "Please fill out all fields" })
      setStatus("DEFAULT")
      return ""
    }

    try {
      const res = await fetch(`${API_URL}/organizations/${orgId}/email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...data,
          patient,
          sendTo: sendToArray,
          body: renderEmail(body, renderingContext),
        }),
      })

      const json = await res.json()
      if (json.error) throw new Error(json.error)

      setStatus("DEFAULT")
      addToast("Success", "Email sent! 📧")
      submitCb?.()
    } catch (error) {
      setStatus("ERROR")
      addToast("Error sending email", (error as Error).message ?? "Unknown error", "error")
    }
  }

  return (
    <div className="flex h-[80vh] w-[80vw] max-w-4xl flex-col">
      <h2 className="flex-shrink-0 text-lg font-semibold">Email patient</h2>

      <div className="my-6 flex-shrink-0">
        <Steps steps={steps} onStepClick={handleStepClick} />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {currentStep === 1 && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden">
              <div className="flex flex-shrink-0 items-center justify-between">
                <h3 className="text-md font-medium">Select Conditions</h3>
                <p className="text-sm text-gray-500"></p>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto pr-2">
                <div className="space-y-4">
                  {conditions?.map((condition) => {
                    const selection = conditionSelections.find((sel) => sel.conditionId === condition._id)

                    return (
                      <div
                        key={condition._id}
                        className={`flex-shrink-0 rounded-md border border-gray-200 bg-gray-50 px-4 py-2 transition-colors`}>
                        <div className="mb-2">
                          <h4 className="font-medium text-gray-900">{condition.name}</h4>
                        </div>

                        <div className="mb-2">
                          <div
                            className="grid gap-2"
                            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                            {condition.recommendations.map((rec, recIndex) => {
                              const isRecommendationSelected =
                                selection?.selectedRecommendationIndex === recIndex
                              return (
                                <button
                                  key={recIndex}
                                  type="button"
                                  onClick={() => handlePresetSelect(condition._id, recIndex)}
                                  className={`focus-visible:ring-primary-500 min-h-20 rounded-md border px-6 py-4 text-sm focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none ${
                                    isRecommendationSelected
                                      ? "border-primary-300 bg-primary-100 text-primary-700 hover:bg-primary-200 hover:border-primary-400"
                                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50"
                                  }`}>
                                  <div className="line-clamp-3 overflow-hidden text-left leading-tight text-ellipsis">
                                    {rec.message}
                                  </div>
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    )
                  })}

                  {(!conditions || conditions.length === 0) && (
                    <div className="flex flex-shrink-0 flex-col items-center rounded-md border border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center">
                      <p className="text-sm text-gray-500">No conditions available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-shrink-0 justify-end gap-x-2 pt-4">
              <Button type="button" onClick={handleNextStep}>
                Next
              </Button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex flex-1 gap-4 overflow-hidden">
              <div className="flex w-1/2 flex-col overflow-hidden">
                <h3 className="mb-2 text-base font-medium">Details</h3>
                <div className="flex-1 space-y-4 overflow-y-auto">
                  <label className="label">
                    Send to (multiple emails separated by commas)
                    <input {...register("sendTo", { required: true })} type="text" className="input" />
                  </label>
                  <label className="label">
                    Reply to
                    <input {...register("replyTo", { required: true })} type="email" className="input" />
                  </label>

                  <label className="label">
                    Subject
                    <input {...register("subject", { required: true })} type="text" className="input" />
                  </label>

                  <label className="label">
                    Email Body
                    <textarea {...register("body")} rows={15} className="input h-auto"></textarea>
                  </label>
                </div>
              </div>

              <div className="flex w-1/2 flex-col overflow-hidden">
                <h3 className="mb-2 text-base font-medium">Preview</h3>
                <div className="flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-white p-6">
                  <div className="text-sm leading-relaxed whitespace-pre-wrap text-gray-900">
                    {renderEmail(body, renderingContext)}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-shrink-0 justify-between gap-x-2 pt-6">
              <Button type="button" variant="outline" onClick={handlePrevStep}>
                Back
              </Button>
              <div className="flex gap-x-2">
                <Button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    reset()
                    submitCb?.()
                  }}
                  disabled={status === "LOADING"}>
                  Cancel
                </Button>
                <Button type="submit" disabled={status === "LOADING"}>
                  Send Email
                </Button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  )
}
